import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Footer } from "@/components/ui/footer";
import { HelmetProvider } from 'react-helmet-async';
import { AppLayout } from "@/components/AppLayout";
import { PrivateLayout } from "@/components/PrivateLayout";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AntiScrapingGuard } from "@/components/AntiScrapingGuard";
import { FAQChatbot } from "@/components/faq/FAQChatbot";
import { SubdomainRedirect } from "@/components/SubdomainRedirect";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import { PWAUpdatePrompt } from "@/components/pwa/PWAUpdatePrompt";
import { PageLoader } from "@/components/ui/PageLoader";

import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { ScrollToTop } from "@/components/ScrollToTop";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Lazy loading de toutes les pages publiques
const Index = lazy(() => import("./pages/Index"));
const MissionsDemandeur = lazy(() => import("./pages/MissionsDemandeur"));
const Prestataires = lazy(() => import("./pages/Prestataires"));
const CartePrestataires = lazy(() => import("./pages/CartePrestataires"));
const Tarifs = lazy(() => import("./pages/Tarifs"));
const Comparatif = lazy(() => import("./pages/Comparatif"));
const CommentCaMarche = lazy(() => import("./pages/CommentCaMarche"));
const APropos = lazy(() => import("./pages/APropos"));
const DeposerMission = lazy(() => import("./pages/DeposerMission"));
const Contact = lazy(() => import("./pages/Contact"));
const ClientOnboarding = lazy(() => import("./pages/ClientOnboarding"));
const PrestataireOnboarding = lazy(() => import("./pages/PrestataireOnboarding"));
const AdminManagement = lazy(() => import("./pages/AdminManagement"));
const Auth = lazy(() => import("./pages/Auth"));
const ProviderForm = lazy(() => import("./pages/ProviderForm"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const RGPD = lazy(() => import("./pages/RGPD"));
const CGU = lazy(() => import("./pages/CGU"));
const CGV = lazy(() => import("./pages/CGV"));
const ChartePrestataire = lazy(() => import("./pages/ChartePrestataire"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Install = lazy(() => import("./pages/Install"));
const Lexique = lazy(() => import("./pages/Lexique"));
const FAQPro = lazy(() => import("./pages/FAQPro"));
const PrestataireDetails = lazy(() => import("./pages/PrestataireDetails"));
const SupportAccess = lazy(() => import("./pages/SupportAccess"));
const MissionResponse = lazy(() => import("./pages/MissionResponse"));

// Lazy loading des pages client
const DashboardClient = lazy(() => import("./pages/DashboardClient"));
const RecapMissions = lazy(() => import("./pages/client/RecapMissions"));
const MesInfosClient = lazy(() => import("./pages/client/MesInfos"));
const AddMissionIA = lazy(() => import("./pages/client/AddMissionIA"));
const AddMissionTexte = lazy(() => import("./pages/client/AddMissionTexte"));
const EditMission = lazy(() => import("./pages/client/EditMission"));
const MotDePasseClient = lazy(() => import("./pages/client/MotDePasse"));
const ClientSupport = lazy(() => import("./pages/client/Support"));

// Lazy loading des pages prestataire
const DashboardPrestataire = lazy(() => import("./pages/DashboardPrestataire"));
const MesInfos = lazy(() => import("./pages/prestataire/MesInfos"));
const MesCompetences = lazy(() => import("./pages/prestataire/MesCompetences"));
const MesContraintes = lazy(() => import("./pages/prestataire/MesContraintes"));
const MotDePasse = lazy(() => import("./pages/prestataire/MotDePasse"));
const Calendrier = lazy(() => import("./pages/prestataire/Calendrier"));
const Missions = lazy(() => import("./pages/prestataire/Missions"));
const ProfilEnrichi = lazy(() => import("./pages/prestataire/ProfilEnrichi"));
const PrestataireProfilPartenaire = lazy(() => import("./pages/prestataire/ProfilPartenaire"));
const EstimationIA = lazy(() => import("./pages/prestataire/EstimationIA"));
const EstimationGestion = lazy(() => import("./pages/prestataire/EstimationGestion"));
const PrestataireSupport = lazy(() => import("./pages/prestataire/Support"));

// Lazy loading des pages admin
const DashboardAdmin = lazy(() => import("./pages/DashboardAdminSimple"));
const PrestatairesAdmin = lazy(() => import("./pages/PrestatairesAdmin"));
const ClientsAdmin = lazy(() => import("./pages/ClientsAdmin"));
const StatsMissions = lazy(() => import("./pages/StatsMissions"));
const WebhookTest = lazy(() => import("./pages/WebhookTest"));
const AdminMissions = lazy(() => import("./pages/AdminMissions"));
const AdminClients = lazy(() => import("./pages/AdminClients"));
const AdminPrestataires = lazy(() => import("./pages/AdminPrestataires"));
const AdminDocuments = lazy(() => import("./pages/AdminDocuments"));
const AdminStats = lazy(() => import("./pages/AdminStats"));
const AdminFAQ = lazy(() => import("./pages/AdminFAQ"));
const AdminPassword = lazy(() => import("./pages/AdminPassword"));
const AdminTOTPVerification = lazy(() => import("./pages/AdminTOTPVerification"));
const AdminsPage = lazy(() => import("./pages/AdminsPage"));
const EditPrestataire = lazy(() => import("./pages/admin/EditPrestataire"));
const Audio = lazy(() => import("./pages/admin/Audio"));
const VoiceConfiguration = lazy(() => import("./pages/admin/VoiceConfiguration"));
const EmbeddingTest = lazy(() => import("./pages/admin/EmbeddingTest"));
const RegenerateEmbeddings = lazy(() => import("./pages/admin/RegenerateEmbeddings"));
const AdminTestModePage = lazy(() => import("./pages/AdminTestModePage"));
const AdminLexique = lazy(() => import("./pages/admin/AdminLexique"));
const AdvancedAnalytics = lazy(() => import("./pages/admin/AdvancedAnalytics"));
const GestionTarifs = lazy(() => import("./pages/admin/GestionTarifs"));
const ReferentielBaseAutomatisee = lazy(() => import("./pages/admin/ReferentielBaseAutomatisee"));
const ProfilPartenaire = lazy(() => import("./pages/admin/ProfilPartenaire"));
const AdminEstimationIA = lazy(() => import("./pages/admin/EstimationIA"));
const LegalDocuments = lazy(() => import("./pages/admin/LegalDocuments"));
const SupportAdmin = lazy(() => import("./pages/admin/SupportAdmin"));
const AnnoncesPromo = lazy(() => import("./pages/admin/AnnoncesPromo"));
const CalendrierAdmin = lazy(() => import("./pages/admin/CalendrierAdmin"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const RechercheHistorique = lazy(() => import("./pages/admin/RechercheHistorique"));
const SystemTools = lazy(() => import("./pages/admin/SystemTools"));
const RechercheEntreprises = lazy(() => import("./pages/admin/RechercheEntreprises"));
const ProspectionNouvellesEntreprises = lazy(() => import("./pages/admin/ProspectionNouvellesEntreprises"));
const RepresentantsSuspects = lazy(() => import("./pages/admin/RepresentantsSuspects"));
const SolvabilityReports = lazy(() => import("./pages/admin/SolvabilityReports"));
const VerificationsSiret = lazy(() => import("./pages/admin/VerificationsSiret"));
const GestionCreditsSolvabilite = lazy(() => import("./pages/admin/GestionCreditsSolvabilite"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <SubdomainRedirect />
          <AntiScrapingGuard>
            <AnalyticsTracker />
            <FAQChatbot />
            <PWAInstallBanner />
            <PWAUpdatePrompt />
            {/* Marker to indicate app has loaded successfully */}
            <div data-app-loaded="true" style={{ display: 'none' }} />
            
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Pages publiques avec AppLayout */}
                <Route path="/" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Index />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/missions" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <MissionsDemandeur />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/prestataires" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Prestataires />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/carte" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <CartePrestataires />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/prestataire/:id" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <PrestataireDetails />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/tarifs" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Tarifs />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/comparatif" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Comparatif />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/comment-ca-marche" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <CommentCaMarche />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/a-propos" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <APropos />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/contact" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Contact />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/auth" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Auth />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/client-onboarding" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <ClientOnboarding />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/prestataire-onboarding" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <PrestataireOnboarding />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/admin-management" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminManagement />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/provider-form" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <ProviderForm />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/reset-password" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <ResetPassword />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/forgot-password" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <ForgotPassword />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/rgpd" element={<RGPD />} />
                <Route path="/cgu" element={<CGU />} />
                <Route path="/cgv" element={<CGV />} />
                <Route path="/charte-prestataire" element={<ChartePrestataire />} />
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/deposer-mission" element={<DeposerMission />} />
                <Route path="/install" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Install />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/lexique" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Lexique />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/faq-pro" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <FAQPro />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                
                {/* Pages privées avec PrivateLayout */}
                <Route path="/dashboard-client" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <DashboardClient />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/recap-missions" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <RecapMissions />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/mes-infos" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <MesInfosClient />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/add-mission-ia" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <AddMissionIA />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/add-mission-texte" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <AddMissionTexte />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/edit-mission/:missionId" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <EditMission />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/mot-de-passe" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <MotDePasseClient />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/dashboard-prestataire" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <DashboardPrestataire />
                  </AuthGuard>
                } />
                
                {/* Prestataire specific pages */}
                <Route path="/prestataire/mes-infos" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <MesInfos />
                  </AuthGuard>
                } />
                <Route path="/prestataire/mes-competences" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <MesCompetences />
                  </AuthGuard>
                } />
                <Route path="/prestataire/mes-contraintes" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <MesContraintes />
                  </AuthGuard>
                } />
                <Route path="/prestataire/mot-de-passe" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <MotDePasse />
                  </AuthGuard>
                } />
                <Route path="/prestataire/missions" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <Missions />
                  </AuthGuard>
                } />
                <Route path="/prestataire/profil-enrichi" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <ProfilEnrichi />
                  </AuthGuard>
                } />
                <Route path="/prestataire/calendrier" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <Calendrier />
                  </AuthGuard>
                } />
                <Route path="/prestataire/profil-partenaire" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrestataireProfilPartenaire />
                  </AuthGuard>
                } />
                <Route path="/prestataire/estimation-add" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <EstimationIA />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/estimation-gestion" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <EstimationGestion />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/r/:token" element={<MissionResponse />} />
                <Route path="/dashboard-admin" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <DashboardAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataires-admin" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <PrestatairesAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/clients-admin" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ClientsAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/statistiques-detaillees" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <StatsMissions />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/webhook-test" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <WebhookTest />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/missions" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminMissions />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/clients" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminClients />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/prestataires" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminPrestataires />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/documents" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminDocuments />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/stats" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminStats />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/faq" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminFAQ />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/password" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminPassword />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/totp-verif" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminTOTPVerification />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/audio" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <Audio />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/admins" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminsPage />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/edit-prestataire" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <EditPrestataire />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/embedding" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <EmbeddingTest />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/regenerate-embeddings" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <RegenerateEmbeddings />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/lexique" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminLexique />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/test-mode" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminTestModePage />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/legal-documents" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <LegalDocuments />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/analytics" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdvancedAnalytics />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/tarifs" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <GestionTarifs />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/profil-partenaire" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ProfilPartenaire />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/estimation-ia" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminEstimationIA />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/rba" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ReferentielBaseAutomatisee />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/support" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <SupportAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/annonces" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AnnoncesPromo />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/calendrier" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <CalendrierAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/marketing" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminMarketing />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/voice-config" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <VoiceConfiguration />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/recherche-historique" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <RechercheHistorique />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/system-tools" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <SystemTools />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/recherche-entreprises" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <RechercheEntreprises />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/prospection-nouvelles-entreprises" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ProspectionNouvellesEntreprises />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/representants-suspects" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <RepresentantsSuspects />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/verifications-siret" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <VerificationsSiret />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/solvability-reports" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <SolvabilityReports />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/credits-solvabilite" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <GestionCreditsSolvabilite />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/support" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <ClientSupport />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/support" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrestataireSupport />
                  </AuthGuard>
                } />
                
                {/* Page d'accès externe aux tickets support */}
                <Route path="/support/access" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <SupportAccess />
                      </div>
                    </div>
                  </AppLayout>
                } />
                
                {/* Page 404 */}
                <Route path="*" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <NotFound />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
              </Routes>
            </Suspense>
          </AntiScrapingGuard>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
